import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, InputBase } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SearchDialog from './SearchDialog';

type SearchProps = {
    placeholder?: string;
    width?: 'full' | 'half' | 'large';
};

const Search = (props: SearchProps) => {
    const [width, setWidth] = useState<string>('100%');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (props.width === 'half') {
            setWidth('50%');
        } else if (props.width === 'large') {
            setWidth('75%');
        } else {
            setWidth('100%');
        }
    }, [props.width]);

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'f')) {
                e.preventDefault();
                setOpen(true);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, []);

    return (
        <Box
            sx={{
                position: 'relative',
                width: width,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Paper
                elevation={0}
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    width: '100%',
                    boxShadow: 'none',
                    height: '100%',
                    cursor: 'text',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s',
                    '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                        borderColor: 'primary.main',
                    },
                }}
            >
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <InputBase
                    placeholder={props.placeholder ?? 'What are you looking for?'}
                    sx={{
                        ml: 1,
                        fontSize: 13,
                        p: 0,
                        flex: 1,
                        pointerEvents: 'none',
                        '& .MuiInputBase-input': {
                            p: 0,
                        },
                    }}
                    size="small"
                    readOnly
                />
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        fontSize={11} 
                        sx={{ 
                            bgcolor: 'background.paper', 
                            border: '1px solid', 
                            borderColor: 'divider', 
                            borderRadius: 1, 
                            px: 0.5,
                            fontWeight: 'bold'
                        }}
                    >
                        ⌘ K
                    </Typography>
                </Box>
            </Paper>

            <SearchDialog open={open} onClose={() => setOpen(false)} />
        </Box>
    );
};

export default Search;
